import React, { FC, ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { defineMessages, useIntl } from "react-intl";

import { CountryRegion, supportedCountries } from "@mapmycustomers/shared";
import { Territory } from "@mapmycustomers/shared/types/entity";
import User from "@mapmycustomers/shared/types/User";
import { nameComparator } from "@mapmycustomers/shared/util/comparator";
import { regionStates } from "@mapmycustomers/shared/util/consts";
import { Modal } from "@mapmycustomers/ui";

import useCountryStateDisplayNames from "@app/scene/map/utils/useCountryStateDisplayNames";
import { getHiddenTerritoriesIds } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { getTerritories } from "@app/store/territories";
import { hideTerritories } from "@app/store/territories/actions";
import useSet from "@app/util/hook/useSet";

import Filters from "./Filters";
import styles from "./FilterTerritoriesModal.module.scss";
import Footer from "./Footer";
import TerritoryItem from "./TerritoryItem";

const messages = defineMessages({
  description: {
    id: "map.layer.boundaries.territories.filterModal.description",
    defaultMessage:
      "Select which territories to visualize on the map so you can stay focused on a particular area. Unselected territories won’t be seen but will still be considered in bucket calculations.",
    description: "Description of the Filter Territories modal",
  },
  title: {
    id: "map.layer.boundaries.territories.filterModal",
    defaultMessage: "Select Visible Territories <stats>{count} / {total}</stats>",
    description: "Title of the Filter Territories modal",
  },
});

export interface FilterTerritoriesModalProps {
  onHide: () => void;
}

interface Props extends FilterTerritoriesModalProps {
  hiddenTerritoryIds: Territory["id"][];
  onUpdateVisibility: typeof hideTerritories;
  territories: Territory[];
}

const FilterTerritoriesModal: FC<Props> = ({
  hiddenTerritoryIds,
  onHide,
  onUpdateVisibility,
  territories,
}) => {
  const intl = useIntl();

  const [hiddenIds, updateHiddenIds, toggleItem, setHiddenIds] = useSet<Territory["id"]>();
  useEffect(() => setHiddenIds(new Set(hiddenTerritoryIds)), [hiddenTerritoryIds, setHiddenIds]);
  const handleSelectionChange = useCallback(
    (territory: Territory, selected: boolean) => toggleItem(territory.id, !selected),
    [toggleItem]
  );

  const handleOk = useCallback(() => {
    onUpdateVisibility(Array.from(hiddenIds));
    onHide();
  }, [onUpdateVisibility, hiddenIds, onHide]);

  const [quickFilter, setQuickFilter] = useState("");
  const [creatorIds, setCreatorIds] = useState<User["id"][]>([]);
  const [region, setRegion] = useState<CountryRegion | undefined>();
  const [sharedWith, setSharedWith] = useState<User["id"][]>([]);
  const [selectedOnly, setSelectedOnly] = useState(false);
  const filteredTerritories = useMemo(() => {
    return territories
      .filter((territory) => {
        const q = quickFilter.trim().toLowerCase();
        if (
          (q && !territory.name.toLowerCase().includes(q)) ||
          (creatorIds.length && !creatorIds.includes(territory.user.id)) ||
          (sharedWith.length && !territory.userIds.some((id) => sharedWith.includes(id)))
        ) {
          return false;
        }
        if (region) {
          const countryStates = new Set(regionStates[region]);
          if (!territory.territoryDetail.region?.some((region) => countryStates.has(region))) {
            return false;
          }
        }
        return selectedOnly ? !hiddenIds.has(territory.id) : true;
      })
      .sort(nameComparator);
  }, [territories, quickFilter, creatorIds, sharedWith, region, selectedOnly, hiddenIds]);

  // only (de)select all among visible territories
  const handleSelectAll = useCallback(() => {
    updateHiddenIds((hiddenIds) => {
      filteredTerritories.forEach((territory) => hiddenIds.delete(territory.id));
      return true;
    });
  }, [updateHiddenIds, filteredTerritories]);
  const handleDeselectAll = useCallback(() => {
    updateHiddenIds((hiddenIds) => {
      filteredTerritories.forEach((territory) => hiddenIds.add(territory.id));
      return true;
    });
  }, [updateHiddenIds, filteredTerritories]);

  const [countryStateDisplayNames] = useCountryStateDisplayNames(supportedCountries);

  return (
    <Modal
      footer={
        <Footer
          onCancel={onHide}
          onDeselectAll={handleDeselectAll}
          onOk={handleOk}
          onSelectAll={handleSelectAll}
          selected={territories.length - hiddenIds.size}
          total={territories.length}
        />
      }
      onCancel={onHide}
      open
      title={intl.formatMessage(messages.title, {
        count: filteredTerritories.length,
        stats: (texts: ReactNode[]) => <span className={styles.stats}>{texts}</span>,
        total: territories.length,
      })}
      width="750px"
    >
      <div className={styles.content}>
        <div className={styles.description}>{intl.formatMessage(messages.description)}</div>
        <Filters
          creatorIds={creatorIds}
          onCreatorIdsChange={setCreatorIds}
          onQuickFilterChange={setQuickFilter}
          onRegionChange={setRegion}
          onSelectedOnlyChange={setSelectedOnly}
          onSharedWithChange={setSharedWith}
          quickFilter={quickFilter}
          region={region}
          selectedOnly={selectedOnly}
          sharedWith={sharedWith}
        />
        <div className={styles.list}>
          {filteredTerritories.map((territory) => (
            <TerritoryItem
              countryStateDisplayNames={countryStateDisplayNames}
              key={territory.id}
              onSelectionChange={handleSelectionChange}
              selected={!hiddenIds.has(territory.id)}
              territory={territory}
            />
          ))}
        </div>
      </div>
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  hiddenTerritoryIds: getHiddenTerritoriesIds(state),
  territories: getTerritories(state),
});

const mapDispatchToProps = {
  onUpdateVisibility: hideTerritories,
};

export default connect(mapStateToProps, mapDispatchToProps)(FilterTerritoriesModal);
